import * as Y from 'yjs';
import { decryptPayload } from '../sync/ws-encryption.js';
import { attachmentReferences, replaceAttachmentReferences } from '../../shared/utils/map-archive.js';
import { t } from '../../shared/i18n/index.js';

function declareBackupRoots(doc) {
  for (const name of ['nodes', 'metadata', 'execution', 'routines']) doc.getMap(name);
  doc.getArray('docNotes');
  for (const name of doc.share.keys()) {
    if (name.startsWith('notes-') || name.startsWith('docnote-')) doc.getXmlFragment(name);
  }
}

export function backupAttachmentReferences(bytes) {
  const doc = new Y.Doc();
  try {
    Y.applyUpdate(doc, bytes);
    declareBackupRoots(doc);
    return attachmentReferences([...doc.share.values()].map(type => type.toJSON()));
  } finally { doc.destroy(); }
}

/** Preserve every readable source; report incomplete exports explicitly. */
export async function mergeBackupState(local, server, key, docId) {
  const doc = new Y.Doc();
  const warnings = [];
  try {
    for (const source of Array.isArray(local) ? local : [local]) {
      if (!source) continue;
      try { Y.applyUpdate(doc, source); }
      catch { warnings.push(t('account.backup.localUnreadable')); }
    }
    const baseline = server?.baseline || server?.state;
    const records = [...(baseline ? [baseline] : []), ...(server?.deltas || [])];
    for (let i = 0; i < records.length; i++) {
      try { Y.applyUpdate(doc, await decryptPayload(records[i], key, docId)); }
      catch { warnings.push(t('account.backup.serverRecordUnreadable', { number: i + 1 })); }
    }
    const bytes = Y.encodeStateAsUpdate(doc);
    return { state: bytes.length > 2 ? bytes : null, warnings };
  } finally { doc.destroy(); }
}

/**
 * Whether a copy on this device holds nothing the server lacks: the server's
 * state with the local copy added is unchanged, content and deletions alike
 * (compared as Yjs snapshots). Pure (tested).
 * @param {Uint8Array[]} serverUpdates the server's records, decrypted
 * @param {Uint8Array} local the local copy
 */
export function serverHasLocalCopy(serverUpdates, local) {
  const server = new Y.Doc();
  const merged = new Y.Doc();
  try {
    for (const update of serverUpdates) {
      Y.applyUpdate(server, update);
      Y.applyUpdate(merged, update);
    }
    Y.applyUpdate(merged, local);
    return Y.equalSnapshots(Y.snapshot(server), Y.snapshot(merged));
  } finally {
    server.destroy();
    merged.destroy();
  }
}

/**
 * The same, from the server's encrypted records (`GET /api/docs/:id/state`).
 * Throws when a record can't be read: then nothing can be concluded.
 */
export async function serverHasLocalCopyEncrypted(local, server, key, docId) {
  const baseline = server?.baseline || server?.state;
  const records = [...(baseline ? [baseline] : []), ...(server?.deltas || [])];
  const updates = [];
  for (const record of records) updates.push(await decryptPayload(record, key, docId));
  return serverHasLocalCopy(updates, local);
}

/** Remap links in the current CRDT while retaining its existing Yjs history. */
export function remapBackupLinks(bytes, docIds, attachmentRefs = new Map(), extraDocNotes = []) {
  const doc = new Y.Doc();
  function value(input, key = '') {
    if (key === 'linkedDocId' && typeof input === 'string') return docIds.get(input) || input;
    if (typeof input === 'string') return replaceAttachmentReferences(input, attachmentRefs);
    if (input instanceof Y.Text) {
      // Links in collaborative notes live in text formatting attributes;
      // images live in XmlElement attrs. Keep all unrelated text/formatting.
      let offset = 0;
      for (const delta of input.toDelta()) {
        const length = typeof delta.insert === 'string' ? delta.insert.length : 1;
        const attrs = value(delta.attributes);
        const insert = value(delta.insert);
        if (insert !== delta.insert) {
          input.delete(offset, length);
          input.insert(offset, insert, attrs);
        } else if (attrs !== delta.attributes && length) input.format(offset, length, attrs);
        offset += typeof insert === 'string' ? insert.length : 1;
      }
      return input;
    }
    if (input instanceof Y.Map) {
      for (const [name, item] of input.entries()) {
        const updated = value(item, name);
        if (updated !== item) input.set(name, updated);
      }
      return input;
    }
    if (input instanceof Y.XmlElement) {
      for (const [name, item] of Object.entries(input.getAttributes())) {
        const updated = value(item, name);
        if (updated !== item) input.setAttribute(name, updated);
      }
      for (const child of input.toArray()) value(child);
      return input;
    }
    if (input instanceof Y.Array || input instanceof Y.XmlFragment) {
      input.toArray().forEach((item, i) => {
        const updated = value(item);
        if (updated !== item) { input.delete(i, 1); input.insert(i, [updated]); }
      });
      return input;
    }
    if (input instanceof Y.AbstractType) return input;
    if (Array.isArray(input)) {
      const result = input.map(item => value(item));
      return result.some((item, i) => item !== input[i]) ? result : input;
    }
    if (input && typeof input === 'object') {
      const entries = Object.entries(input).map(([name, item]) => [name, value(item, name)]);
      return entries.some(([name, item]) => item !== input[name]) ? Object.fromEntries(entries) : input;
    }
    return input;
  }
  try {
    Y.applyUpdate(doc, bytes);
    // Declare known maps before reading. Yjs cannot infer a root type from an
    // update alone. Other roots are left intact in the encoded state.
    declareBackupRoots(doc);
    doc.transact(() => {
      for (const type of doc.share.values()) value(type);
      if (extraDocNotes.length) {
        doc.getArray('docNotes').push(extraDocNotes);
        if (doc.getMap('metadata').get('docNotesFormat') === 'yxml-v1') {
          for (const note of extraDocNotes) {
            const fragment = doc.getXmlFragment(`docnote-${note.id}`);
            for (const match of note.content.matchAll(/^\[(.*)\]\((attachment:[^)]+)\)$/gm)) {
              const paragraph = new Y.XmlElement('paragraph');
              const text = new Y.XmlText();
              paragraph.insert(0, [text]); fragment.push([paragraph]);
              text.insert(0, match[1], { link: { href: match[2] } });
            }
          }
        }
      }
    });
    return Y.encodeStateAsUpdate(doc);
  } finally { doc.destroy(); }
}
